import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import ApiClient from '../../utils/apiClient.js';

/**
 * NotificationDetails component for displaying full notification details
 * with revision history and diff capabilities
 */
const NotificationDetails = ({
  notification,
  onEdit,
  onRefresh,
  onClose
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [selectedRevisions, setSelectedRevisions] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Initialize API client
  const apiClient = new ApiClient(getToken);

  /**
   * Fetch revision history for the notification
   */
  const fetchRevisions = async () => {
    if (!notification?.id) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/api/admin/notifications/${notification.id}/revisions`);
      
      if (response.success) {
        setRevisions(response.revisions || []);
      } else {
        throw new Error(response.message || 'Failed to load revision history');
      }
    } catch (error) {
      console.error('Failed to load revision history:', error);
      showToast('Failed to load revision history', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle revision selection for comparison
   */
  const handleRevisionSelect = (revision) => {
    setSelectedRevisions(prev => {
      if (prev.find(r => r.id === revision.id)) {
        return prev.filter(r => r.id !== revision.id);
      } else if (prev.length < 2) {
        return [...prev, revision];
      } else {
        return [prev[1], revision];
      }
    });
  };

  /**
   * Handle revision revert
   */
  const handleRevert = async (revision) => {
    if (!confirm(`Are you sure you want to revert to revision from ${formatDate(revision.created_at)}?`)) {
      return;
    }

    try {
      setReverting(true);
      const response = await apiClient.post(`/api/admin/notifications/${notification.id}/revert/${revision.revision_number}`);
      
      if (response.success) {
        showToast('Notification reverted successfully', 'success');
        onRefresh();
        fetchRevisions(); // Refresh revision history
      } else {
        throw new Error(response.message || 'Failed to revert notification');
      }
    } catch (error) {
      console.error('Failed to revert notification:', error);
      showToast(error.message || 'Failed to revert notification', 'error');
    } finally {
      setReverting(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  /**
   * Get change type badge styling
   */
  const getChangeTypeBadge = (changeReason) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    if (!changeReason) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
          : 'bg-gray-100 text-gray-800 border border-gray-200'
      }`;
    }

    const reason = changeReason.toLowerCase();
    
    if (reason.includes('created')) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
          : 'bg-green-100 text-green-800 border border-green-200'
      }`;
    } else if (reason.includes('updated') || reason.includes('edited')) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30' 
          : 'bg-blue-100 text-blue-800 border border-blue-200'
      }`;
    } else if (reason.includes('activated') || reason.includes('published')) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-teal-900/30 text-teal-400 border border-teal-500/30' 
          : 'bg-teal-100 text-teal-800 border border-teal-200'
      }`;
    } else if (reason.includes('deactivated') || reason.includes('unpublished')) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-red-900/30 text-red-400 border border-red-500/30' 
          : 'bg-red-100 text-red-800 border border-red-200'
      }`;
    }
    
    return `${baseClasses} ${
      theme === 'dark' 
        ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
        : 'bg-gray-100 text-gray-800 border border-gray-200'
    }`;
  };

  /**
   * Render field comparison for diff view
   */
  const renderFieldComparison = (field, oldValue, newValue) => {
    if (oldValue === newValue) return null;

    return (
      <div key={field} className={`border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} rounded-lg p-4`}>
        <h5 className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2 capitalize`}>
          {field.replace(/_/g, ' ')}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Old Value</div>
            <div className={`${
              theme === 'dark' 
                ? 'bg-red-900/20 border-red-500/30 text-gray-300' 
                : 'bg-red-50 border-red-200 text-red-800'
            } border rounded p-2 text-sm whitespace-pre-wrap`}>
              {oldValue || 'Empty'}
            </div>
          </div>
          <div>
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>New Value</div>
            <div className={`${
              theme === 'dark' 
                ? 'bg-green-900/20 border-green-500/30 text-gray-300' 
                : 'bg-green-50 border-green-200 text-green-800'
            } border rounded p-2 text-sm whitespace-pre-wrap`}>
              {newValue || 'Empty'}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
/**
   * Render revision comparison
   */
  const renderComparison = () => {
    if (selectedRevisions.length !== 2) return null;

    const [older, newer] = selectedRevisions.sort((a, b) => a.revision_number - b.revision_number);
    const fields = ['title', 'body', 'is_active'];

    return (
      <div className={`mt-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 shadow-sm`}>
        <div className="flex justify-between items-center mb-4">
          <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Revision Comparison</h4>
          <button
            onClick={() => {
              setShowComparison(false);
              setSelectedRevisions([]);
            }}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-xl`}
          >
            ✕
          </button>
        </div>
        
        <div className={`mb-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Comparing revision {older.revision_number} from {formatDate(older.created_at)} with revision {newer.revision_number} from {formatDate(newer.created_at)}
        </div>

        <div className="space-y-4">
          {fields.map(field => 
            renderFieldComparison(field, older[field], newer[field])
          ).filter(Boolean)}
          
          {fields.every(field => older[field] === newer[field]) && (
            <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className="text-2xl mb-2">🔍</div>
              <p>No differences found between these revisions.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Load revisions when notification changes
  useEffect(() => {
    if (notification?.id) {
      fetchRevisions();
    }
  }, [notification?.id]);

  if (!notification) {
    return null;
  }

  return (
    <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-start`}>
        <div className="flex-1">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
            Notification Details
          </h3>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              notification.is_active
                ? theme === 'dark'
                  ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                  : 'bg-green-100 text-green-800 border border-green-200'
                : theme === 'dark'
                  ? 'bg-red-900/30 text-red-400 border border-red-500/30'
                  : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {notification.is_active ? 'Active' : 'Inactive'}
            </span>
            {notification.current_revision > 1 && (
              <span className={`px-2 py-1 rounded text-xs ${
                theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                Revision {notification.current_revision}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRevisions.length === 2 && (
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
            >
              {showComparison ? 'Hide' : 'Show'} Comparison
            </button>
          )}
          <button
            onClick={() => onEdit(notification)}
            className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
          >
            Edit
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-xl`}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Notification Content */}
      <div className="p-6">
        <div className="mb-6">
          <h4 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
            {notification.title}
          </h4>
          <div className={`${
            theme === 'dark' 
              ? 'bg-gray-700 border-gray-600 text-gray-300' 
              : 'bg-gray-50 border-gray-200 text-gray-700'
          } border rounded-lg p-4 whitespace-pre-wrap`}>
            {notification.body}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              Created
            </label>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {formatDate(notification.created_at)}
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              Last Updated
            </label>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {formatDate(notification.updated_at)}
            </div>
          </div>
        </div>   
     {/* Revision History Section */}
        <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} pt-6`}>
          <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
            Revision History
          </h4>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto"></div>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-4`}>Loading revision history...</p>
            </div>
          ) : revisions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📝</div>
              <h5 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>No Revisions Found</h5>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                This notification has no revision history available.
              </p>
            </div>
          ) : (
            <>
              {/* Instructions */}
              <div className={`mb-6 ${
                theme === 'dark' 
                  ? 'bg-blue-900/20 border-blue-500/30 text-blue-300' 
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              } border rounded-lg p-4`}>
                <p className="text-sm">
                  Select up to 2 revisions to compare changes. Click "Revert" to restore a previous version.
                </p>
              </div>

              {/* Revisions list */}
              <div className="space-y-4">
                {revisions.map((revision, index) => (
                  <div
                    key={revision.id}
                    className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                      selectedRevisions.find(r => r.id === revision.id)
                        ? theme === 'dark'
                          ? 'border-teal-500 bg-teal-900/20'
                          : 'border-teal-500 bg-teal-50'
                        : theme === 'dark'
                          ? 'border-gray-600 hover:border-gray-500'
                          : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => handleRevisionSelect(revision)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={getChangeTypeBadge(revision.change_reason)}>
                            {revision.change_reason || 'Updated'}
                          </span>
                          <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Revision {revision.revision_number}
                          </span>
                          <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {formatDate(revision.created_at)}
                          </span>
                          {index === 0 && (
                            <span className={`px-2 py-1 ${
                              theme === 'dark' 
                                ? 'bg-teal-900/30 text-teal-400 border-teal-500/30' 
                                : 'bg-teal-100 text-teal-800 border-teal-200'
                            } border rounded-full text-xs font-medium`}>
                              Current
                            </span>
                          )}
                        </div>
                        
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                          <span className="font-medium">Title:</span> {revision.title}
                        </div>

                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                          <span className="font-medium">Status:</span> {revision.is_active ? 'Active' : 'Inactive'}
                        </div>

                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          Body: {revision.body.length > 100 ? `${revision.body.substring(0, 100)}...` : revision.body}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {selectedRevisions.find(r => r.id === revision.id) && (
                          <span className={`${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'} text-sm`}>Selected</span>
                        )}
                        {index > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRevert(revision);
                            }}
                            disabled={reverting}
                            className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:opacity-50 dark:text-white"
                          >
                            {reverting ? 'Reverting...' : 'Revert'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Comparison panel */}
              {showComparison && renderComparison()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDetails;
